import React from 'react';
import { ChevronDown, Clock, MapPin, Calendar, Users, Mail, UserCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations, escapeRooms } from '../data/mock';
import { RoomCard } from '../components/RoomCard';

export const HomePage = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const scrollToRooms = () => {
    document.getElementById('rooms')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-navy-dark">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy-darker via-navy-dark to-accent/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gold rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-5xl">
          {/* Premium Symmetrical Hourglass */}
          <div className="flex justify-center mb-8">
            <div className="premium-hourglass">
              <svg width="100" height="140" viewBox="0 0 100 140" className="hourglass-svg">
                {/* Glass frame - perfectly symmetrical */}
                <g className="hourglass-frame">
                  <path d="M25 10 L75 10 L75 20 L60 60 L75 100 L75 130 L25 130 L25 100 L40 60 L25 20 Z" 
                        fill="none" stroke="#d4af37" strokeWidth="3" opacity="0.95"/>
                  {/* Inner glass shine */}
                  <path d="M28 13 L72 13 L72 20 L58 58 L72 98 L72 127 L28 127 L28 98 L42 58 L28 20 Z" 
                        fill="none" stroke="#fff" strokeWidth="1" opacity="0.3"/>
                </g>
                
                {/* Top chamber sand */}
                <path className="sand-top-chamber" d="M30 15 L70 15 L70 20 L58 55 L42 55 L30 20 Z" 
                      fill="url(#sandGradient)" opacity="0.9"/>
                
                {/* Bottom chamber sand */}
                <path className="sand-bottom-chamber" d="M30 125 L70 125 L70 100 L50 70 Z" 
                      fill="url(#sandGradient)" opacity="0.9"/>
                
                {/* Flowing sand particles - stream */}
                <g className="sand-flow">
                  <circle className="sand-drop sand-drop-1" cx="50" cy="58" r="1" fill="#ffd700" opacity="0.9"/>
                  <circle className="sand-drop sand-drop-2" cx="50" cy="63" r="1" fill="#ffd700" opacity="0.85"/>
                  <circle className="sand-drop sand-drop-3" cx="50" cy="68" r="0.8" fill="#d4af37" opacity="0.8"/>
                  <circle className="sand-drop sand-drop-4" cx="49.5" cy="73" r="0.8" fill="#d4af37" opacity="0.75"/>
                  <circle className="sand-drop sand-drop-5" cx="50.5" cy="78" r="0.7" fill="#d4af37" opacity="0.7"/>
                </g>
                
                {/* Gradients */}
                <defs>
                  <linearGradient id="sandGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#ffd700" stopOpacity="1"/>
                    <stop offset="50%" stopColor="#f4c430" stopOpacity="1"/>
                    <stop offset="100%" stopColor="#d4af37" stopOpacity="1"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
          
          {/* Animated Countdown to T-MINUS */}
          <div className="mb-8 animate-fade-in">
            <h1 className="countdown-display text-gold mb-4">
              T-MINUS
            </h1>
          </div>
          
          <p className="body-large text-cream max-w-2xl mx-auto mb-12 animate-fade-in-delay">
            {t.hero.motto}
          </p>
          <button
            onClick={scrollToRooms}
            className="btn-primary animate-fade-in-delay-2"
          >
            {language === 'en' ? 'Explore Our Rooms' : 'Découvrez Nos Salles'}
          </button>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={scrollToRooms}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce"
        >
          <ChevronDown className="w-8 h-8 text-gold" />
        </button>
      </section>

      {/* Escape Rooms Section */}
      <section id="rooms" className="py-24 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="heading-1 text-gold mb-4">{t.rooms.title}</h2>
            <p className="body-medium text-cream/80">{t.rooms.subtitle}</p>
          </div>

          {/* Room Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {escapeRooms.map((room) => (
              <RoomCard key={room.id} room={room} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6 bg-navy-darker">
        <div className="container mx-auto max-w-7xl">
          <div className="bg-navy-dark border border-gold/20 rounded-2xl p-12 max-w-4xl mx-auto">
            <h3 className="heading-2 text-gold mb-8 text-center">{t.rooms.howItWorks.title}</h3>
            <div className="space-y-6 text-cream/90 body-small leading-relaxed whitespace-pre-line">
              {t.rooms.howItWorks.content}
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="flex flex-col items-center text-center p-6 bg-accent/10 rounded-xl border border-accent/30">
                <MapPin className="w-12 h-12 text-gold mb-4" />
                <h4 className="heading-6 text-cream mb-2">{language === 'en' ? 'Anywhere' : 'Partout'}</h4>
                <p className="caption text-cream/70">{language === 'en' ? 'We come to you' : 'Nous venons à vous'}</p>
              </div>
              <div className="flex flex-col items-center text-center p-6 bg-accent/10 rounded-xl border border-accent/30">
                <Clock className="w-12 h-12 text-gold mb-4" />
                <h4 className="heading-6 text-cream mb-2">{language === 'en' ? '1 Hour' : '1 Heure'}</h4>
                <p className="caption text-cream/70">{language === 'en' ? '1 hour of thrills' : 'Une heure d\'immersion'}</p>
              </div>
              <div className="flex flex-col items-center text-center p-6 bg-accent/10 rounded-xl border border-accent/30">
                <Calendar className="w-12 h-12 text-gold mb-4" />
                <h4 className="heading-6 text-cream mb-2">{language === 'en' ? 'Your Date' : 'Votre Date'}</h4>
                <p className="caption text-cream/70">{language === 'en' ? 'Book when it suits you' : 'Réservez quand cela vous convient'}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-6 max-w-2xl mx-auto">
              <div className="flex flex-col items-center text-center p-6 bg-accent/10 rounded-xl border border-accent/30">
                <Users className="w-12 h-12 text-gold mb-4" />
                <h4 className="heading-6 text-cream mb-2">{language === 'en' ? '3-6 Players' : '3-6 Joueurs'}</h4>
                <p className="caption text-cream/70">{language === 'en' ? 'Per room' : 'Par salle'}</p>
              </div>
              <div className="flex flex-col items-center text-center p-6 bg-accent/10 rounded-xl border border-accent/30">
                <UserCheck className="w-12 h-12 text-gold mb-4" />
                <h4 className="heading-6 text-cream mb-2">{language === 'en' ? 'Ages 10+' : 'Âges 10+'}</h4>
                <p className="caption text-cream/70">{language === 'en' ? 'Fun for all ages' : 'Amusant pour tous'}</p>
              </div>
            </div>
          </div>

          {/* Bulk Event Quote Section */}
          <div className="mt-16 bg-navy-dark border-2 border-gold/30 rounded-2xl p-12 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="heading-2 text-gold mb-4">{t.rooms.bulkEvent.title}</h3>
              <p className="body-medium text-cream/80">{t.rooms.bulkEvent.subtitle}</p>
            </div>
            
            <form className="space-y-6" onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              console.log('Bulk quote request:', Object.fromEntries(formData));
              alert(t.quote.success);
              e.target.reset();
            }}>
              <div>
                <label className="block body-small text-cream mb-2">
                  <Mail className="inline w-4 h-4 mr-2" />
                  {t.quote.email}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 bg-navy-darker border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                  placeholder={language === 'en' ? 'your@email.com' : 'votre@email.com'}
                />
              </div>

              <div>
                <label className="block body-small text-cream mb-2">
                  <Calendar className="inline w-4 h-4 mr-2" />
                  {t.quote.eventType}
                </label>
                <select
                  name="eventType"
                  required
                  className="w-full px-4 py-3 bg-navy-darker border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                >
                  <option value="">{language === 'en' ? 'Select event type' : 'Sélectionnez le type d\'événement'}</option>
                  <option value="private">{t.quote.privateParty}</option>
                  <option value="school">{t.quote.schoolEvent}</option>
                  <option value="office">{t.quote.officeEvent}</option>
                </select>
              </div>

              <div>
                <label className="block body-small text-cream mb-2">
                  <Users className="inline w-4 h-4 mr-2" />
                  {t.quote.players}
                </label>
                <input
                  type="number"
                  name="players"
                  required
                  min="1"
                  className="w-full px-4 py-3 bg-navy-darker border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                  placeholder="20"
                />
              </div>

              <div>
                <label className="block body-small text-cream mb-2">
                  <MapPin className="inline w-4 h-4 mr-2" />
                  {t.quote.location}
                </label>
                <input
                  type="text"
                  name="location"
                  required
                  className="w-full px-4 py-3 bg-navy-darker border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                  placeholder={language === 'en' ? '123 Main St, City' : '123 rue Principale, Ville'}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block body-small text-cream mb-2">
                    <Calendar className="inline w-4 h-4 mr-2" />
                    {t.quote.date}
                  </label>
                  <input
                    type="date"
                    name="date"
                    required
                    className="w-full px-4 py-3 bg-navy-darker border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                  />
                </div>

                <div>
                  <label className="block body-small text-cream mb-2">
                    <Clock className="inline w-4 h-4 mr-2" />
                    {t.quote.time}
                  </label>
                  <input
                    type="time"
                    name="time"
                    required
                    className="w-full px-4 py-3 bg-navy-darker border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary w-full">
                {t.quote.submit}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Prizes Section */}
      <section id="prizes" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/5 via-transparent to-accent/5"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <h2 className="heading-1 text-gold text-center mb-16">{t.rooms.prizes.title}</h2>
          <div className="grid md:grid-cols-2 gap-12">
            {/* Breakout Prize - 10% */}
            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-gold/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
              <div className="relative bg-navy-darker border-2 border-gold/40 rounded-2xl p-12 text-center hover:border-gold transition-all duration-300 hover:scale-105 h-full flex flex-col">
                <div className="flex justify-center mb-8">
                  <div className="w-32 h-32 bg-gradient-to-br from-gold/20 to-gold/10 rounded-full flex items-center justify-center border-4 border-gold shadow-lg shadow-gold/50">
                    <span className="text-5xl font-bold text-gold countdown-font">10%</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gold mb-4 countdown-font">{t.rooms.prizes.breakout}</h3>
                <p className="body-medium text-cream/80 leading-relaxed flex-grow">{t.rooms.prizes.breakoutDesc}</p>
              </div>
            </div>

            {/* Record Prize - 20% */}
            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-br from-gold/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
              <div className="relative bg-navy-darker border-2 border-gold/40 rounded-2xl p-12 text-center hover:border-gold transition-all duration-300 hover:scale-105 h-full flex flex-col">
                <div className="flex justify-center mb-8">
                  <div className="w-32 h-32 bg-gradient-to-br from-gold/20 to-gold/10 rounded-full flex items-center justify-center border-4 border-gold shadow-lg shadow-gold/50">
                    <span className="text-5xl font-bold text-gold countdown-font">20%</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gold mb-4 countdown-font">{t.rooms.prizes.record}</h3>
                <p className="body-medium text-cream/80 leading-relaxed flex-grow">{t.rooms.prizes.recordDesc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About & Contact Section */}
      <section id="about" className="py-24 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="heading-1 text-gold text-center mb-16">{t.about.title}</h2>
          
          {/* Mission */}
          <div className="bg-navy-darker border border-gold/20 rounded-2xl p-12 mb-12">
            <h3 className="text-3xl font-bold text-gold mb-6 countdown-font">{t.about.mission}</h3>
            <p className="body-medium text-cream/90 leading-relaxed">{t.about.missionText}</p>
          </div>

          {/* Contact */}
          <div className="text-center">
            <h3 className="heading-4 text-gold mb-6">{t.about.inquiry}</h3>
            <a
              href={`mailto:${t.about.email}`}
              className="inline-flex items-center gap-2 text-2xl text-cream hover:text-gold transition-colors duration-300 font-semibold"
            >
              {t.about.email}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};